// Editing (docs/project.md §7; Edit boards): the whole row of overrides via PUT /entries/{id};
// a spelling change goes through PUT /entries/{id}/headword first.
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { api } from '@/api/endpoints';
import { ApiRequestError } from '@/api/client';
import { db } from '@/db/db';
import { useCard } from '@/db/hooks';
import { applyMutationRows } from '@/sync/apply';
import { puller } from '@/sync/pull';
import { useDictionary } from '@/state/session';
import { useOnline } from '@/state/online';
import { EntryForm, cleanDraft, draftFromEntry, type EntryDraft } from '@/components/EntryForm';
import { ErrorText, Sheet, SheetBar, useCloseOverlay } from '@/components/ui';
import { reportError } from '@/lib/errors';
import { overrideChanges, track } from '@/lib/analytics';

export const Route = createFileRoute('/_app/d/$dictionaryId/w/$entryId/edit')({
  component: EditScreen,
});

function EditScreen() {
  const { dictionaryId, entryId } = Route.useParams();
  const t = useTranslations('edit');
  const tc = useTranslations('common');
  const online = useOnline();
  const dictionary = useDictionary(dictionaryId);
  const data = useCard(dictionary, entryId);
  const close = useCloseOverlay({ to: '/d/$dictionaryId/w/$entryId', params: { dictionaryId, entryId } });
  const [draftState, setDraft] = useState<EntryDraft | null>(null);
  // The draft starts from the current row once it is loaded from Dexie
  const draft = draftState ?? (data ? draftFromEntry(data.lexeme.headword, data.entry) : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [headwordError, setHeadwordError] = useState<string | null>(null);

  if (!dictionary) return null;

  const save = async () => {
    if (!draft || !data) return;
    const clean = cleanDraft(draft);
    if (!clean.headword) {
      setHeadwordError(t('errors.headwordRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    setHeadwordError(null);
    try {
      const headwordChanged = clean.headword !== data.lexeme.headword;
      if (headwordChanged) {
        const res = await api.entries.headword(entryId, clean.headword);
        await applyMutationRows(db, res);
      }
      const overrides = {
        transcription: clean.transcription,
        partOfSpeech: clean.partOfSpeech,
        translations: clean.translations,
        examples: clean.examples,
        related: clean.related,
      };
      const res = await api.entries.update(entryId, overrides);
      await applyMutationRows(db, { entry: res.entry });
      const { entry } = data;
      const before = { transcription: entry.transcription, partOfSpeech: entry.partOfSpeech, translations: entry.translations, examples: entry.examples, related: entry.related };
      track('entry_edited', {
        headword: data.lexeme.headword,
        headword_changed: headwordChanged,
        new_headword: clean.headword,
        ...overrideChanges(before, overrides),
      });
      void puller.pull(dictionaryId);
      close();
    } catch (e) {
      reportError(e);
      if (e instanceof ApiRequestError && e.code === 'already_in_dictionary') setHeadwordError(t('errors.already_in_dictionary'));
      else setError(e instanceof ApiRequestError && e.isNetwork ? tc('errorNetwork') : tc('errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet onClose={close} label={t('title')} width="max-w-[640px]">
      <SheetBar
        left={
          <button type="button" className="h-11 px-1 text-[17px] text-accent" onClick={close}>
            {tc('cancel')}
          </button>
        }
        title={t('title')}
        right={
          <button type="button" className="h-11 px-1 text-[17px] font-semibold text-accent disabled:opacity-40" disabled={busy || !online || !draft} data-testid="save" onClick={() => void save()}>
            {tc('save')}
          </button>
        }
      />
      <div className="flex flex-grow flex-col gap-4 overflow-y-auto px-4 pb-8 pt-2">
        {draft && data ? (
          <EntryForm
            mode="edit"
            draft={draft}
            onChange={setDraft}
            shared={{ lexeme: data.lexeme, gloss: data.gloss }}
            targetLang={dictionary.targetLang}
            baseLang={dictionary.baseLang}
            headwordError={headwordError}
          />
        ) : null}
        <ErrorText>{error}</ErrorText>
      </div>
    </Sheet>
  );
}
